/**
 * Installed-packs API slice.
 *
 * GET /api/v1/packs/installed — which packs is the current tenant on?
 * Drives PackContext / usePacks() → Sidebar conditional rendering and
 * /crm/* route guards.
 *
 * instanceId is passed as an EXPLICIT query arg (not relying solely on
 * the baseApi auto-injector) so it lands in the RTK Query cache key.
 * This is the deliberate discovery from the frontend audit: pure auto-
 * inject keys the cache by the static URL "/packs/installed", so a
 * Redux-driven instance switch (General Settings flow, no page reload)
 * would serve the PREVIOUS tenant's pack list. With instanceId in the
 * arg, the switch produces a new cache key → automatic refetch. The
 * Header account switcher does a full page reload, so it remounts
 * clean regardless; the explicit arg is what makes the no-reload path
 * correct. baseQueryWithInstanceId sees params.instanceId and does NOT
 * double-append — same wire format either way.
 *
 * `mode` and `sidebarSections` are resolved server-side from the pack
 * manifest registry; the full manifest never crosses the wire — only
 * the display-safe nav projection (lib/packs/wire.ts, type-only import
 * here so no manifest values are bundled client-side). Unknown pack_ids
 * are filtered server-side, so every row here is fully specified.
 */

import { baseApi } from "./baseApi";
import type {
  WireSidebarSection,
  WireCustomFieldDefinition,
  WireLeadProfile,
} from "../../../lib/packs/wire";
import type { TerminologyOverrides } from "../../../lib/packs/terminology";
import type {
  CustomFieldEntity,
  CustomFieldOption,
  CustomFieldType,
  PackFeatureId,
} from "../../../lib/packs/types";

export type PackMode = "lead_gen_only" | "operator_crm";

export type { WireSidebarItem, WireSidebarSection } from "../../../lib/packs/wire";
export type { WireCustomFieldDefinition } from "../../../lib/packs/wire";

/** Resolved custom-field definitions per entity (GET /packs/fields). */
export type PackFieldsByEntity = Partial<Record<CustomFieldEntity, WireCustomFieldDefinition[]>>;

/** A tenant-defined custom field def row (LB-2, GET /skycrm/custom-field-defs). */
export interface TenantCustomFieldDef {
  id: string;
  entity: CustomFieldEntity;
  slug: string;
  label: string;
  type: CustomFieldType;
  required: boolean;
  options: CustomFieldOption[];
  display_section: string;
  display_order: number;
  created_at: string;
  updated_at: string;
  /** Non-null when soft-deleted (deactivated); restore clears it. */
  deleted_at: string | null;
}

/** Create payload — slug is the BARE slug; the server prepends `custom_`. */
export interface CreateTenantCustomFieldDefArgs {
  instanceId: string;
  entity: CustomFieldEntity;
  slug: string;
  label: string;
  type: CustomFieldType;
  required: boolean;
  options?: CustomFieldOption[];
  display_section?: string;
  display_order?: number;
}

export interface UpdateTenantCustomFieldDefArgs {
  instanceId: string;
  id: string;
  label?: string;
  required?: boolean;
  options?: CustomFieldOption[];
  display_section?: string;
  display_order?: number;
}

export interface InstalledPack {
  packId: string;
  version: string;
  installedAt: string | null;
  mode: PackMode;
  /** Display-safe nav projection of the pack manifest (single source of truth for pack navigation). */
  sidebarSections: WireSidebarSection[];
  /** Entity-noun overrides (Phase-1 terminology service); layered over PLATFORM_TERMS in install order. */
  terminology?: TerminologyOverrides;
  /** In-page feature capabilities (E2 vertex gate); consume via usePacks().hasFeature(id). */
  features?: PackFeatureId[];
  /**
   * The display-safe lead-profile subset (vertical-lead-profile §3.1). Absent for a
   * pack that declares no profile. Consume via usePacks().leadProfile.
   */
  leadProfile?: WireLeadProfile;
}

export type { WireLeadProfile } from "../../../lib/packs/wire";

/** Catalog entry for the Setup → Packs page: registry metadata + install status. */
export interface PackCatalogEntry {
  packId: string;
  name: string;
  description: string;
  /** Registry (latest) version. */
  version: string;
  mode: PackMode;
  installed: boolean;
  installedVersion: string | null;
  installedAt: string | null;
}

export const packsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getInstalledPacks: builder.query<InstalledPack[], { instanceId: string }>({
      query: ({ instanceId }) => ({
        url: "/packs/installed",
        params: { instanceId },
      }),
      providesTags: ["InstalledPacks"],
    }),
    getPacksCatalog: builder.query<PackCatalogEntry[], { instanceId: string }>({
      query: ({ instanceId }) => ({
        url: "/packs/catalog",
        params: { instanceId },
      }),
      providesTags: ["InstalledPacks"],
    }),
    /**
     * Resolved custom-field DEFINITIONS for the tenant's installed packs (Phase-1
     * runtime). Feeds PackContext → usePackCustomFields(entity) → the dynamic
     * form. Definitions only — a person's VALUES come from the person detail read.
     * Same install-order source as getInstalledPacks, so it shares its tag.
     *
     * `communityId` (optional) narrows vocab-backed options (care_types) to what
     * that community OFFERS — the picker fix. It is part of the ARG, so RTK Query
     * keys the cache by it: a form where the user changes community (the Add
     * Prospect wizard) re-resolves its options instead of serving the previous
     * community's — the very reason this could not be a one-shot instance-wide
     * fetch. Omitted → instance-wide, byte-identical to before (which is what
     * PackContext, the app-shell consumer, keeps doing).
     */
    getPackFields: builder.query<
      { fields: PackFieldsByEntity },
      { instanceId: string; communityId?: string | null }
    >({
      query: ({ instanceId, communityId }) => ({
        url: "/packs/fields",
        params: communityId ? { instanceId, communityId } : { instanceId },
      }),
      providesTags: ["InstalledPacks"],
    }),
    /** Tenant terminology layer (Phase-1). Merged over pack overrides in PackContext. */
    getTenantTerminology: builder.query<
      { overrides: TerminologyOverrides },
      { instanceId: string }
    >({
      query: ({ instanceId }) => ({
        url: "/skycrm/terminology",
        params: { instanceId },
      }),
      providesTags: ["TenantTerminology"],
    }),
    updateTenantTerminology: builder.mutation<
      { overrides: TerminologyOverrides },
      { instanceId: string; overrides: Record<string, string | null> }
    >({
      query: ({ instanceId, overrides }) => ({
        url: `/skycrm/terminology?instanceId=${instanceId}`,
        method: "PUT",
        body: { overrides },
      }),
      invalidatesTags: ["TenantTerminology"],
    }),

    /**
     * Tenant-defined custom field defs (LB-2). GET is open to any instance
     * user; mutations are admin-gated server-side. Every mutation invalidates
     * BOTH TenantCustomFieldDefs (the editor list) AND InstalledPacks — the
     * latter is the tag getPackFields provides, so an open person/episode form
     * refetches and picks up (or drops) the field without a reload.
     */
    getTenantCustomFieldDefs: builder.query<
      { defs: TenantCustomFieldDef[] },
      { instanceId: string; entity?: CustomFieldEntity }
    >({
      query: ({ instanceId, entity }) => ({
        url: "/skycrm/custom-field-defs",
        params: entity ? { instanceId, entity } : { instanceId },
      }),
      providesTags: ["TenantCustomFieldDefs"],
    }),
    createTenantCustomFieldDef: builder.mutation<
      { def: TenantCustomFieldDef },
      CreateTenantCustomFieldDefArgs
    >({
      query: ({ instanceId, ...body }) => ({
        url: `/skycrm/custom-field-defs?instanceId=${instanceId}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["TenantCustomFieldDefs", "InstalledPacks"],
    }),
    updateTenantCustomFieldDef: builder.mutation<
      { def: TenantCustomFieldDef },
      UpdateTenantCustomFieldDefArgs
    >({
      query: ({ instanceId, id, ...body }) => ({
        url: `/skycrm/custom-field-defs/${id}?instanceId=${instanceId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["TenantCustomFieldDefs", "InstalledPacks"],
    }),
    deleteTenantCustomFieldDef: builder.mutation<
      { def: TenantCustomFieldDef },
      { instanceId: string; id: string }
    >({
      query: ({ instanceId, id }) => ({
        url: `/skycrm/custom-field-defs/${id}?instanceId=${instanceId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["TenantCustomFieldDefs", "InstalledPacks"],
    }),
    restoreTenantCustomFieldDef: builder.mutation<
      { def: TenantCustomFieldDef },
      { instanceId: string; id: string }
    >({
      query: ({ instanceId, id }) => ({
        url: `/skycrm/custom-field-defs/${id}/restore?instanceId=${instanceId}`,
        method: "POST",
      }),
      invalidatesTags: ["TenantCustomFieldDefs", "InstalledPacks"],
    }),
  }),
});

export const {
  useGetInstalledPacksQuery,
  useGetPacksCatalogQuery,
  useGetPackFieldsQuery,
  useGetTenantTerminologyQuery,
  useUpdateTenantTerminologyMutation,
  useGetTenantCustomFieldDefsQuery,
  useCreateTenantCustomFieldDefMutation,
  useUpdateTenantCustomFieldDefMutation,
  useDeleteTenantCustomFieldDefMutation,
  useRestoreTenantCustomFieldDefMutation,
} = packsApi;
